import BaseService from './BaseService';
import apiClient from '../plugins/axios';

/**
 * LiveKit Service
 */
class LivekitService extends BaseService {
  constructor() {
    super('livekit');
  }

  /**
   * Get LiveKit token
   * @param {string} room - Room name (required)
   * @param {string} [identity] - User identity (optional)
   * @param {string} [phone] - Phone number (optional)
   * @returns {Promise<Object>}
   */
  async getToken(room, identity, phone) {
    try {
      const params = new URLSearchParams({ room });

      if (identity) params.append('identity', identity);
      if (phone) params.append('phone', phone);

      const response = await apiClient.get(
        `/livekit/token?${params.toString()}`,
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Dispatch agent to a room
   * @param {Object} data
   * @param {string} data.room - Room name (required)
   * @param {string} [data.phone]
   * @param {string} [data.agent_name]
   * @param {string} [data.agent_id]
   * @returns {Promise<Object>}
   */
  async dispatchAgent(data) {
    try {
      const response = await apiClient.post(
        '/livekit/dispatch',
        data,
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List SIP inbound trunks (LiveKit SIP API proxy).
   */
  async listInboundTrunks() {
    try {
      const response = await apiClient.get('/livekit/sip/inbound-trunks');
      const raw = response.data?.data ?? response.data;
      if (Array.isArray(raw)) return raw;
      if (raw?.items && Array.isArray(raw.items)) return raw.items;
      return [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create SIP inbound trunk.
   * @param {Object} body - Matches CreateSIPInboundTrunkBody (e.g. { trunk: { name, auth_username, ... } })
   */
  async createInboundTrunk(body) {
    try {
      const response = await apiClient.post('/livekit/sip/inbound-trunks', body);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete SIP inbound trunk by LiveKit trunk id (e.g. ST_...).
   */
  async deleteInboundTrunk(sipTrunkId) {
    try {
      const id = encodeURIComponent(sipTrunkId);
      const response = await apiClient.delete(`/livekit/sip/inbound-trunks/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * GET inbound trunk detail by LiveKit id (ST_…).
   */
  async getInboundTrunk(sipTrunkId) {
    try {
      const id = encodeURIComponent(sipTrunkId);
      const response = await apiClient.get(`/livekit/sip/inbound-trunks/${id}`);
      const raw = response.data?.data ?? response.data;
      return raw;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * PATCH inbound trunk (body: UpdateSIPInboundTrunkBody — replace | update).
   */
  async patchInboundTrunk(sipTrunkId, body) {
    try {
      const id = encodeURIComponent(sipTrunkId);
      const response = await apiClient.patch(`/livekit/sip/inbound-trunks/${id}`, body);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List SIP dispatch rules (LiveKit SIP API proxy).
   */
  async listDispatchRules() {
    try {
      const response = await apiClient.get('/livekit/sip/dispatch-rules');
      const raw = response.data?.data ?? response.data;
      if (Array.isArray(raw)) return raw;
      if (raw?.items && Array.isArray(raw.items)) return raw.items;
      return [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create SIP dispatch rule (trunk_ids required; use IDs from listed trunks).
   * @param {Object} body - CreateSIPDispatchRuleBody
   */
  async createDispatchRule(body) {
    try {
      const response = await apiClient.post('/livekit/sip/dispatch-rules', body);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete SIP dispatch rule by LiveKit rule id (e.g. SDR_...).
   */
  async deleteDispatchRule(sipDispatchRuleId) {
    try {
      const id = encodeURIComponent(sipDispatchRuleId);
      const response = await apiClient.delete(`/livekit/sip/dispatch-rules/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * GET dispatch rule detail by LiveKit id (SDR_…).
   */
  async getDispatchRule(sipDispatchRuleId) {
    try {
      const id = encodeURIComponent(sipDispatchRuleId);
      const response = await apiClient.get(`/livekit/sip/dispatch-rules/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * PATCH dispatch rule (body: UpdateSIPDispatchRuleBody — replace | update).
   */
  async patchDispatchRule(sipDispatchRuleId, body) {
    try {
      const id = encodeURIComponent(sipDispatchRuleId);
      const response = await apiClient.patch(`/livekit/sip/dispatch-rules/${id}`, body);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new LivekitService();
